import { FC, useCallback, useEffect, useState } from 'react';

import './AdminUsers.css';
import { AdminUser, ApiError, deleteUser, fetchAdminUsers } from '../../api/client';
import { Button, Card, CardTitle, Dialog, EmptyState, Meta, Notice, SectionHeader, StatusBadge } from '../../ui';

type AdminUsersProps = {
  /** The signed-in username, so the account cannot delete itself from here. */
  currentUsername: string | null;
  onUnauthorized: () => void;
};

/** Surface a failure, or hand an expired session back to App to log out. */
function report(error: unknown, onUnauthorized: () => void, setError: (message: string) => void, fallback: string) {
  if (error instanceof ApiError && error.status === 401) {
    onUnauthorized();
    return;
  }
  setError(error instanceof Error ? error.message : fallback);
}

function joined(iso: string): string {
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? iso : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "1 sale", "2 sales": the counts shown before a destructive delete. */
function count(total: number, noun: string): string {
  return total + ' ' + noun + (total === 1 ? '' : 's');
}

/**
 * Every account on this install, and the admin's destructive controls.
 *
 * SettingsPage always renders this embedded in the grouped Settings shell, so
 * the section header steps down under GroupPage's h1 and there is no standalone
 * page or back control. The header, notice, dialog, badge, empty state, card,
 * buttons and the visually-hidden label come from src/ui; what is left here is
 * the accounts table's own column and phone composition.
 */
const AdminUsers: FC<AdminUsersProps> = ({ currentUsername, onUnauthorized }) => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  /** The account whose delete is awaiting a second click. */
  const [confirmId, setConfirmId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    fetchAdminUsers()
      .then((result) => {
        if (cancelled) return;
        setUsers(result);
        setError('');
      })
      .catch((err: unknown) => {
        if (!cancelled) report(err, onUnauthorized, setError, 'Could not load the accounts.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [onUnauthorized]);

  const remove = useCallback(
    (userId: number) => {
      setBusyId(userId);
      deleteUser(userId)
        .then(() => {
          setUsers((current) => current.filter((entry) => entry.id !== userId));
          setConfirmId(null);
          setError('');
        })
        .catch((err: unknown) => {
          report(err, onUnauthorized, setError, 'Could not delete the account.');
          setConfirmId(null);
        })
        .finally(() => setBusyId(null));
    },
    [onUnauthorized]
  );

  const pending = users.find((entry) => entry.id === confirmId) ?? null;

  return (
    <div className="adminUsers">
      <SectionHeader
        title="Users"
        description={`${count(users.length, 'account')} · deleting one removes its catalogue, events, bundles and sales`}
      />

      {error && (
        <Notice variant="error" onDismiss={() => setError('')}>
          {error}
        </Notice>
      )}

      <Card className="adminUsersCard">
        <CardTitle>Accounts</CardTitle>
        {loading ? (
          <Meta role="status">loading accounts…</Meta>
        ) : users.length === 0 ? (
          <EmptyState title="No accounts yet." />
        ) : (
          <table className="adminUsersTable">
            <thead>
              <tr>
                <th align="left">User</th>
                <th align="left">Joined</th>
                <th>Sales</th>
                <th>Items</th>
                <th>Events</th>
                <th align="right">
                  <span className="ui-visually-hidden">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {users.map((entry) => {
                const isSelf = entry.username === currentUsername;
                return (
                  <tr key={entry.id}>
                    <td align="left">
                      {entry.username}
                      {entry.is_admin && <StatusBadge className="adminUsersBadge">admin</StatusBadge>}
                    </td>
                    <td align="left">{joined(entry.created_at)}</td>
                    <td align="center">{entry.sales}</td>
                    <td align="center">{entry.items}</td>
                    <td align="center">{entry.events}</td>
                    <td align="right">
                      {isSelf ? (
                        <Meta as="span">you</Meta>
                      ) : (
                        <Button variant="destructive" size="sm" onClick={() => setConfirmId(entry.id)}>
                          delete
                        </Button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          // A delete already in flight must finish; Escape and the backdrop do
          // not dismiss it while the account is being removed.
          if (!open && busyId === null) setConfirmId(null);
        }}
        title={pending ? `Delete ${pending.username}?` : 'Delete account?'}
        description={
          pending
            ? `This removes its catalogue, ${count(pending.sales, 'sale')}, ${count(pending.items, 'item')} and ${count(pending.events, 'event')}. It cannot be undone.`
            : undefined
        }
        tone="danger"
        actions={
          <>
            <Button variant="secondary" disabled={busyId !== null} onClick={() => setConfirmId(null)}>
              cancel
            </Button>
            <Button
              variant="destructive"
              disabled={busyId !== null}
              onClick={() => {
                if (pending) remove(pending.id);
              }}
            >
              {busyId === pending?.id ? 'deleting…' : 'delete everything'}
            </Button>
          </>
        }
      />
    </div>
  );
};

export default AdminUsers;
